/**
 * SubscribeSection component handles the subscription form and interactions.
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onCloseHandler - Function to call when the subscription is successful and the form should close.
 * @param {Array} props.selectedOptions - Array of selected subscription options.
 * @returns {JSX.Element} The rendered SubscribeSection component.
 */

import React, { useContext, useState } from "react";
import { Button } from "../common";
import axios from "axios";
import { Plausible } from "@/providers/PlausibleProvider";
import { PLAUSIBLE_EVENTS } from "@/utils/constants";

export default function SubscribeSection({ onCloseHandler, selectedOptions = [] }) {
  const [email, setEmail] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isEmailProcessing, setIsEmailProcessing] = useState(false);

  const handleInputChange = (event) => {
    if (errMsg?.length > 0) {
      setErrMsg("");
    }
    setEmail(event.target.value);
    // Check if the entered email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(event.target.value));
  };

  const apiUrl = `${process.env.NEXT_PUBLIC_WEB_BASE_URL}/keep-me-posted`;

  const { plausibleEvent } = useContext(Plausible);

  const sendEmail = async () => {
    setIsEmailProcessing(true);
    setErrMsg("");
    try {
      const response = await axios.post(apiUrl, { email, selectedOptions });
      const data = response?.data;
      setSuccessMsg("You're on the list!");
      plausibleEvent(PLAUSIBLE_EVENTS.MAILING_SIGNUP);

      setIsEmailProcessing(false);

      setTimeout(() => {
        setSuccessMsg("");
        setEmail("");

        onCloseHandler?.();
      }, 5000);

      if (!data?.success) {
        const err = data?.error?.error_data?.[0]?.msg;
        setIsEmailProcessing(false);
        setErrMsg(err);
      } else {
        localStorage.setItem("isSubscribed", "true"); // Set isSubscribed flag here
      }
    } catch (error) {
      setIsEmailProcessing(false);
      setErrMsg(error.response?.data?.error?.error_data?.[0]?.msg);
    }
  };

  const handleSubmit = () => {
    if (email.trim() === "") {
      setErrMsg("Enter a valid email address");
      return;
    }
    if (isValidEmail) {
      sendEmail();
    } else {
      setIsEmailProcessing(false);
      setErrMsg("The login email you entered is invalid");
    }
  };
  return (
    <div className="flex flex-col mt-[30px] w-full md:w-[90%]">
      {successMsg?.length > 0 ? (
        <h6 className="flex justify-center items-center h-[66.4px] lg:h-[123px]">
          {successMsg}
        </h6>
      ) : (
        <div className="flex flex-col">
          <input
            type="text"
            className={`outline-none bg-[#f2f2f2] w-full rounded-full px-6 lg:px-4 py-[18px] mb-[15px] h-[58px] text-center`}
            value={email}
            placeholder="Enter your email address"
            onChange={handleInputChange}
          />
          <Button onClickFn={handleSubmit} disabled={isEmailProcessing}>
            <div
              className={`eyebrow-bold transition-colors w-full tracking-wide`}
            >
              {isEmailProcessing ? "Processing" : "Subscribe"}
            </div>
          </Button>
        </div>
      )}

      {errMsg?.length > 0 && (
        <div className="text-error small-p h-[20px] py-[16px] px-[25px] lg:px-0">
          {errMsg}
        </div>
      )}
    </div>
  );
}
